import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { batchService } from '../services/batchService';

// Async thunks
export const fetchAllBatches = createAsyncThunk(
    'batches/fetchAll',
    async (params = {}, { rejectWithValue }) => {
        try {
            return await batchService.getAllBatches(params);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatchesByPatient = createAsyncThunk(
    'batches/fetchByPatient',
    async (patientId, { rejectWithValue }) => {
        try {
            return await batchService.getBatchesByPatient(patientId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatch = createAsyncThunk(
    'batches/fetchOne',
    async (id, { rejectWithValue }) => {
        try {
            return await batchService.getBatch(id);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const createBatch = createAsyncThunk(
    'batches/create',
    async (data, { rejectWithValue }) => {
        try {
            return await batchService.createBatch(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const updateBatch = createAsyncThunk(
    'batches/update',
    async ({ id, updates }, { rejectWithValue }) => {
        try {
            return await batchService.updateBatch(id, updates);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const deleteBatch = createAsyncThunk(
    'batches/delete',
    async (id, { rejectWithValue }) => {
        try {
            await batchService.deleteBatch(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const addDocumentToBatch = createAsyncThunk(
    'batches/addDocument',
    async ({ batchId, documentId, position }, { rejectWithValue }) => {
        try {
            return await batchService.addDocumentToBatch(batchId, documentId, position);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const removeDocumentFromBatch = createAsyncThunk(
    'batches/removeDocument',
    async ({ batchId, documentId }, { rejectWithValue }) => {
        try {
            return await batchService.removeDocumentFromBatch(batchId, documentId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const reorderDocuments = createAsyncThunk(
    'batches/reorder',
    async ({ batchId, documentIds }, { rejectWithValue }) => {
        try {
            return await batchService.reorderDocuments(batchId, documentIds);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const consolidateBatch = createAsyncThunk(
    'batches/consolidate',
    async (batchId, { rejectWithValue }) => {
        try {
            return await batchService.consolidateBatch(batchId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const sendBatch = createAsyncThunk(
    'batches/send',
    async (payload, { rejectWithValue }) => {
        try {
            const { batchId, ...sendOverrides } = typeof payload === 'string'
                ? { batchId: payload }
                : (payload || {});
            if (!batchId) {
                throw new Error('Batch ID is required to send a batch');
            }
            return await batchService.sendBatch(batchId, sendOverrides);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const createSuperBatch = createAsyncThunk(
    'batches/createSuper',
    async (batchIds, { rejectWithValue }) => {
        try {
            return await batchService.createSuperBatch(batchIds);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchSuperBatches = createAsyncThunk(
    'batches/fetchSuper',
    async (params = {}, { rejectWithValue }) => {
        try {
            return await batchService.getSuperBatches(params);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatchesByClaimId = createAsyncThunk(
    'batches/fetchByClaimId',
    async (claimId, { rejectWithValue }) => {
        try {
            return await batchService.getBatchByClaimId(claimId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatchHistoryByClaimId = createAsyncThunk(
    'batches/fetchHistoryByClaimId',
    async (claimId, { rejectWithValue }) => {
        try {
            return await batchService.getBatchHistoryByClaimId(claimId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatchHistoryByPatientId = createAsyncThunk(
    'batches/fetchHistoryByPatientId',
    async (patientId, { rejectWithValue }) => {
        try {
            return await batchService.getBatchHistoryByPatientId(patientId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

export const fetchBatchHistoryByInsuranceClaimId = createAsyncThunk(
    'batches/fetchHistoryByInsuranceClaimId',
    async (insuranceClaimId, { rejectWithValue }) => {
        try {
            return await batchService.getBatchHistoryByInsuranceClaimId(insuranceClaimId);
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    }
);

const batchesSlice = createSlice({
    name: 'batches',
    initialState: {
        batches: [],
        superBatches: [],
        currentBatch: null,
        batchForClaim: null,
        batchHistory: null,
        batchHistoryLoading: false,
        loading: false,
        superBatchLoading: false,
        error: null,
        batchPagination: { total: 0, page: 1, pageSize: 50, totalPages: 1 },
        superBatchPagination: { total: 0, page: 1, pageSize: 50, totalPages: 1 },
    },
    reducers: {
        selectBatch: (state, action) => {
            state.currentBatch = action.payload;
        },
        clearCurrentBatch: (state) => {
            state.currentBatch = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearBatchForClaim: (state) => {
            state.batchForClaim = null;
        },
        clearBatchHistory: (state) => {
            state.batchHistory = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all batches
            .addCase(fetchAllBatches.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllBatches.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload || {};
                state.batches = Array.isArray(payload) ? payload : (payload.data || []);
                state.batchPagination = Array.isArray(payload) ? {
                    total: payload.length,
                    page: 1,
                    pageSize: payload.length,
                    totalPages: 1,
                } : {
                    total: payload.total || 0,
                    page: payload.page || 1,
                    pageSize: payload.pageSize || 50,
                    totalPages: payload.totalPages || 1,
                };
            })
            .addCase(fetchAllBatches.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch batches by patient
            .addCase(fetchBatchesByPatient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBatchesByPatient.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload || {};
                state.batches = Array.isArray(payload) ? payload : (payload.data || []);
            })
            .addCase(fetchBatchesByPatient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch single batch
            .addCase(fetchBatch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBatch.fulfilled, (state, action) => {
                state.loading = false;
                state.currentBatch = action.payload;
            })
            .addCase(fetchBatch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create batch
            .addCase(createBatch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBatch.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && typeof action.payload === 'object' && action.payload.id) {
                    state.batches.unshift(action.payload);
                    state.currentBatch = action.payload;
                } else {
                    state.error = 'Invalid batch data received';
                }
            })
            .addCase(createBatch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update batch
            .addCase(updateBatch.fulfilled, (state, action) => {
                const index = state.batches.findIndex(b => b.id === action.payload.id);
                if (index !== -1) {
                    state.batches[index] = action.payload;
                }
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = action.payload;
                }
            })
            // Delete batch
            .addCase(deleteBatch.fulfilled, (state, action) => {
                state.batches = state.batches.filter(b => b.id !== action.payload);
                // Also remove from superBatches if present
                state.superBatches = state.superBatches.filter(b => b.id !== action.payload);

                if (state.currentBatch?.id === action.payload) {
                    state.currentBatch = null;
                }
            })
            // Add document
            .addCase(addDocumentToBatch.fulfilled, (state, action) => {
                const index = state.batches.findIndex(b => b.id === action.payload.id);
                if (index !== -1) {
                    state.batches[index] = action.payload;
                }
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = action.payload;
                }
            })
            // Remove document
            .addCase(removeDocumentFromBatch.fulfilled, (state, action) => {
                const index = state.batches.findIndex(b => b.id === action.payload.id);
                if (index !== -1) {
                    state.batches[index] = action.payload;
                }
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = action.payload;
                }
            })
            // Reorder documents
            .addCase(reorderDocuments.fulfilled, (state, action) => {
                const index = state.batches.findIndex(b => b.id === action.payload.id);
                if (index !== -1) {
                    state.batches[index] = action.payload;
                }
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = action.payload;
                }
            })
            // Consolidate batch
            .addCase(consolidateBatch.pending, (state) => {
                state.loading = true;
            })
            .addCase(consolidateBatch.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.batches.findIndex(b => b.id === action.payload.id);
                if (index !== -1) {
                    state.batches[index] = action.payload;
                }
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = action.payload;
                }
            })
            .addCase(consolidateBatch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create Super Batch
            .addCase(createSuperBatch.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createSuperBatch.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && typeof action.payload === 'object' && action.payload.id) {
                    state.batches.unshift(action.payload); // Add new super batch to list
                    state.currentBatch = action.payload;
                } else {
                    state.error = 'Invalid super batch data received';
                }
            })
            .addCase(createSuperBatch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch super batches
            .addCase(fetchSuperBatches.pending, (state) => {
                state.superBatchLoading = true;
                state.error = null;
            })
            .addCase(fetchSuperBatches.fulfilled, (state, action) => {
                state.superBatchLoading = false;
                const payload = action.payload || {};
                state.superBatches = Array.isArray(payload) ? payload : (payload.data || []);
                state.superBatchPagination = Array.isArray(payload) ? {
                    total: payload.length,
                    page: 1,
                    pageSize: payload.length,
                    totalPages: 1,
                } : {
                    total: payload.total || 0,
                    page: payload.page || 1,
                    pageSize: payload.pageSize || 50,
                    totalPages: payload.totalPages || 1,
                };
            })
            .addCase(fetchSuperBatches.rejected, (state, action) => {
                state.superBatchLoading = false;
                state.error = action.payload;
            })
            // Send batch
            .addCase(sendBatch.pending, (state) => {
                state.loading = true;
            })
            .addCase(sendBatch.fulfilled, (state, action) => {
                state.loading = false;
                // Remove the batch from the local array to prevent it from appearing
                // in both 'ready' and 'sent' tabs during the transition
                const { batchId } = action.payload || {};
                if (batchId) {
                    // Remove from batches array - the fetchAllBatches call will re-add
                    // it to the correct filtered list
                    state.batches = state.batches.filter(b => b.id !== batchId);
                    if (state.currentBatch?.id === batchId) {
                        state.currentBatch = null;
                    }
                }
            })
            .addCase(sendBatch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch batch by claim ID
            .addCase(fetchBatchesByClaimId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBatchesByClaimId.fulfilled, (state, action) => {
                state.loading = false;
                state.batchForClaim = action.payload || null;
            })
            .addCase(fetchBatchesByClaimId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.batchForClaim = null;
            })
            // Fetch batch history by claim ID
            .addCase(fetchBatchHistoryByClaimId.pending, (state) => {
                state.batchHistoryLoading = true;
                state.error = null;
            })
            .addCase(fetchBatchHistoryByClaimId.fulfilled, (state, action) => {
                state.batchHistoryLoading = false;
                state.batchHistory = action.payload;
            })
            .addCase(fetchBatchHistoryByClaimId.rejected, (state, action) => {
                state.batchHistoryLoading = false;
                state.error = action.payload;
                state.batchHistory = null;
            })
            // Fetch batch history by patient ID
            .addCase(fetchBatchHistoryByPatientId.pending, (state) => {
                state.batchHistoryLoading = true;
                state.error = null;
            })
            .addCase(fetchBatchHistoryByPatientId.fulfilled, (state, action) => {
                state.batchHistoryLoading = false;
                state.batchHistory = action.payload;
            })
            .addCase(fetchBatchHistoryByPatientId.rejected, (state, action) => {
                state.batchHistoryLoading = false;
                state.error = action.payload;
                state.batchHistory = null;
            })
            // Fetch batch history by insurance claim ID
            .addCase(fetchBatchHistoryByInsuranceClaimId.pending, (state) => {
                state.batchHistoryLoading = true;
                state.error = null;
            })
            .addCase(fetchBatchHistoryByInsuranceClaimId.fulfilled, (state, action) => {
                state.batchHistoryLoading = false;
                state.batchHistory = action.payload;
            })
            .addCase(fetchBatchHistoryByInsuranceClaimId.rejected, (state, action) => {
                state.batchHistoryLoading = false;
                state.error = action.payload;
                state.batchHistory = null;
            });
    },
});

export const { selectBatch, clearCurrentBatch, clearError, clearBatchForClaim, clearBatchHistory } = batchesSlice.actions;
export default batchesSlice.reducer;
